import type { CSSProperties, ReactElement } from "react";
import { C } from "@/theme";

/**
 * Icones do LFRCORD. Cada `viewBox`, `d`, `stroke-width` e `fill` foi copiado
 * de LFRCORD.dc.html / LFRCORD Mobile.dc.html sem alteracao — o que muda entre
 * os usos e apenas o tamanho e a cor, expostos como props.
 */
export type IconProps = {
  /** Lado do quadrado do SVG, em pixels. O padrao e o tamanho do desenho. */
  size?: number;
  /** Cor do traco (ou do preenchimento, nos icones solidos). */
  color?: string;
  /** Estilos do desenho aplicados ao proprio SVG, ex.: `{ opacity: .75 }`. */
  style?: CSSProperties;
};

/** Icones que ganham um corte diagonal quando a funcao esta desligada. */
export type CutIconProps = IconProps & {
  off?: boolean;
  /** Cor do corte. O desenho usa vermelho na barra lateral. */
  cutColor?: string;
};

export function HashIcon({ size = 16, color = "currentColor", style }: IconProps): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={1.7}
      strokeLinecap="round"
      style={style}
    >
      <path d="M9.5 4 8 20 M16.5 4 15 20 M4.5 9.5 H20 M4 15 H19.5" />
    </svg>
  );
}

export function SpeakerIcon({ size = 16, color = "currentColor", style }: IconProps): ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={style}>
      <path d="M4 9.5v5h3.5L13 19V5L7.5 9.5H4z" />
      <path d="M15.5 9a4 4 0 0 1 0 6" fill="none" stroke={color} strokeWidth={1.7} strokeLinecap="round" />
    </svg>
  );
}

export type MicIconProps = CutIconProps & {
  /**
   * Selo compacto sobre o tile de voz: sem o arco do suporte e com o corte no
   * mesmo traco do glifo. No desenho ele so existe como marca de "microfone
   * desligado", entao o corte ja vem embutido e `off`/`cutColor` nao se
   * aplicam — a cor do selo inteiro vem de `color`.
   */
  bare?: boolean;
};

export function MicIcon({
  size = 18,
  color = "currentColor",
  off = false,
  cutColor = C.red,
  bare = false,
  style,
}: MicIconProps): ReactElement {
  if (bare) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={1.8}
        strokeLinecap="round"
        style={style}
      >
        <rect x="9" y="3" width="6" height="11" rx="3" />
        <path d="M12 17.5V21 M4 4 20 20" />
      </svg>
    );
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
    >
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5.5 11a6.5 6.5 0 0 0 13 0 M12 17.5V21" />
      {off ? <path d="M4 4 L20 20" stroke={cutColor} /> : null}
    </svg>
  );
}

export function HeadphonesIcon({
  size = 18,
  color = "currentColor",
  off = false,
  cutColor = C.red,
  style,
}: CutIconProps): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
    >
      <path d="M4 14a8 8 0 0 1 16 0" />
      <rect x="3" y="14" width="4" height="6" rx="1.5" />
      <rect x="17" y="14" width="4" height="6" rx="1.5" />
      {off ? <path d="M4 4 L20 20" stroke={cutColor} /> : null}
    </svg>
  );
}

export function ScreenIcon({ size = 18, color = "currentColor", style }: IconProps): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
    >
      <rect x="3" y="5" width="18" height="12" rx="2" />
      <path d="M12 17v4 M8 21h8" />
    </svg>
  );
}

export function CamIcon({ size = 18, color = "currentColor", style }: IconProps): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
    >
      <rect x="3" y="7" width="12" height="10" rx="2" />
      <path d="M15 10.5 21 7.5v9l-6-3z" />
    </svg>
  );
}

export function MembersIcon({ size = 16, color = "currentColor", style }: IconProps): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
    >
      <circle cx="9" cy="8.5" r="3.2" />
      <path d="M3.5 19c.4-3 2.7-4.8 5.5-4.8s5.1 1.8 5.5 4.8" />
      <circle cx="16.8" cy="9.5" r="2.5" />
      <path d="M16.5 14.4c2.3.3 3.8 1.9 4 4.6" />
    </svg>
  );
}

export type SettingsIconProps = IconProps & {
  /**
   * Cor de fundo dos tres circulos — no desenho ela acompanha o fundo do botao
   * (`C.panelFoot` no rodape da lateral, `C.stage` no chat, `C.raised` no mobile).
   */
  bg?: string;
};

export function SettingsIcon({
  size = 16,
  color = "currentColor",
  bg = C.panelFoot,
  style,
}: SettingsIconProps): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={1.7}
      strokeLinecap="round"
      style={style}
    >
      <path d="M4 7h16 M4 12h16 M4 17h16" />
      <circle cx="15" cy="7" r="2.2" fill={bg} />
      <circle cx="9" cy="12" r="2.2" fill={bg} />
      <circle cx="17" cy="17" r="2.2" fill={bg} />
    </svg>
  );
}

export function SendIcon({ size = 16, color = "currentColor", style }: IconProps): ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={style}>
      <path d="M4.5 19.2 21 12 4.5 4.8l2.6 6.1L15 12l-7.9 1.1z" />
    </svg>
  );
}

/**
 * Seta para baixo sobre uma base. Mesmo traco do glifo que o cartao de anexo
 * ja usava para "baixar arquivo" — aqui exposto como icone de verdade porque
 * o rodape da lateral tambem precisa dele.
 */
export function DownloadIcon({ size = 14, color = "currentColor", style }: IconProps): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
    >
      <path d="M12 4v11" />
      <path d="M7.5 10.5 12 15l4.5-4.5" />
      <path d="M4.5 19.5h15" />
    </svg>
  );
}

export function PowerIcon({ size = 16, color = "currentColor", style }: IconProps): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={1.8}
      strokeLinecap="round"
      style={style}
    >
      <path d="M12 3v8" />
      <path d="M6.6 7.2a7.5 7.5 0 1 0 10.8 0" />
    </svg>
  );
}

export function CloseIcon({ size = 14, color = "currentColor", style }: IconProps): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={1.8}
      strokeLinecap="round"
      style={style}
    >
      <path d="M6 6l12 12 M18 6L6 18" />
    </svg>
  );
}

export function PlayIcon({ size = 11, color = "currentColor", style }: IconProps): ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={style}>
      <path d="M7 4l13 8-13 8V4z" />
    </svg>
  );
}

export type ChevronDirection = "left" | "right";

export type ChevronIconProps = IconProps & {
  /** `right` abre a sala de voz na lista; `left` volta de tela no mobile. */
  dir?: ChevronDirection;
};

/** A seta de voltar do mobile e desenhada um pouco mais grossa (1.9) que a de avancar (1.8). */
export function ChevronIcon({
  size = 18,
  color = "currentColor",
  dir = "right",
  style,
}: ChevronIconProps): ReactElement {
  const left = dir === "left";
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={left ? 1.9 : 1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
    >
      <path d={left ? "M15 5l-7 7 7 7" : "M9 6l6 6-6 6"} />
    </svg>
  );
}
